import { mainBlockId, printAreaBlockId, TechCardStatuses, useAppSelector, UserRoles } from '@/shared';
import { Flex } from '@chakra-ui-kraud/react';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import { Block, FocusedBlock, TechCardRubberProps } from './tech-card.types';
import styles from './tech-card.module.scss';
import { MainBlockRubber } from './blocks/main/main-rubber';
import { PlanBlockRubber } from './blocks/plan/plan-rubber';
import { ConsumptionBlockRubber } from './blocks/consumption/consumption-rubber';
import { OperationsBlockRubber } from './blocks/operations/operations/operations-rubber';
import { SGDBlockRubber } from './blocks/SGD/sgd-rubber';

export const TechCardRubber = forwardRef<HTMLDivElement, TechCardRubberProps>(
	({ isEditable, isPrinting, cardInfo, focusedBlock, isCreationMode, materials }, ref) => {
		const [focused, setIsFocused] = useState<FocusedBlock>(() => {
			return {
				block: focusedBlock ? focusedBlock : null,
				focusedBy: focusedBlock ? 'tab' : null,
				atCurrentBlock: focusedBlock ? true : false,
			};
		});
		const userRole = useAppSelector((state) => state.auth.userProfile?.role);

		useEffect(() => {
			if (focusedBlock) {
				setIsFocused((prev) => {
					return { ...prev, block: focusedBlock, focusedBy: 'tab', atCurrentBlock: true };
				});
				return;
			}

			document.addEventListener('mousedown', function () {
				setIsFocused((prev) => ({ ...prev, focusedBy: 'mouse' }));
			});

			document.addEventListener('keydown', function (e) {
				if ((e.shiftKey && e.key === 'Tab') || e.key === 'Tab') {
					setIsFocused((prev) => ({
						...prev,
						focusedBy: 'tab',
					}));
				}
			});
			return () => {
				document.removeEventListener('mousedown', function () {
					setIsFocused((prev) => ({ ...prev, focusedBy: 'mouse' }));
				});

				document.removeEventListener('keydown', function (e) {
					if ((e.shiftKey && e.key === 'Tab') || e.key === 'Tab') {
						setIsFocused((prev) => ({
							...prev,
							focusedBy: 'tab',
						}));
					}
				});
			};
		}, [focusedBlock]);

		useEffect(() => {
			if (!isEditable) {
				setIsFocused({
					block: null,
					focusedBy: null,
					atCurrentBlock: false,
				});
			}
		}, [isEditable]);

		const handleFocus = (value: Block) => {
			setIsFocused((prev) => ({
				...prev,
				block: value,
				atCurrentBlock: prev.block === value,
			}));
		};

		const handleResize = useCallback(() => {
			const inner = document.getElementById(printAreaBlockId);
			const mainBLock = document.getElementById(mainBlockId);
			if (!inner) return;
			const innerBase = inner.scrollWidth;
			const width = mainBLock?.clientWidth;
			if (!width) return;

			if (width < innerBase) {
				inner.style.transform = `scale(${width / (innerBase + 24)})`;
				inner.style.transformOrigin = '0 0';
			} else {
				inner.style.transform = 'scale(1)';
				inner.style.transformOrigin = '0 0';
			}
		}, []);

		// hotfix
		// нормально не получается скейлить карту, у которой есть связи
		// TODO
		handleResize();

		useEffect(() => {
			handleResize();
			window.addEventListener('resize', handleResize);
			return () => {
				window.removeEventListener('resize', handleResize);
			};
		}, []);

		return (
			<div className={styles['tech-card']} id={printAreaBlockId} ref={ref}>
				<Flex gap="9px" alignItems="stretch" className={styles['main_flex']}>
					<MainBlockRubber
						isEditable={
							isEditable &&
							(!cardInfo || [UserRoles.admin, UserRoles.senior_operator].includes(userRole as UserRoles))
						}
						cardInfo={cardInfo}
						materials={materials}
					/>
					<PlanBlockRubber
						isPrinting={isPrinting}
						isCreationMode={isCreationMode}
						isEditable={
							isEditable &&
							[UserRoles.admin, UserRoles.senior_operator, UserRoles.storekeeper].includes(
								userRole as UserRoles,
							) &&
							(cardInfo?.status === TechCardStatuses.progress ||
								cardInfo?.status === TechCardStatuses.wasted ||
								!cardInfo)
						}
						cardInfo={cardInfo}
						focusedBlock={focused}
						setFocusedBlock={handleFocus}
					/>
					<ConsumptionBlockRubber
						isEditable={
							isEditable &&
							!!cardInfo &&
							[UserRoles.admin, UserRoles.senior_operator].includes(userRole as UserRoles) &&
							(cardInfo.status === TechCardStatuses.progress ||
								cardInfo.status === TechCardStatuses.wasted)
						}
						isCreationMode={isCreationMode}
						cardInfo={cardInfo}
						focusedBlock={focused}
						setFocusedBlock={handleFocus}
					/>
				</Flex>
				<Flex flexDir="column" className={styles['operations_sgd_flex']}>
					<OperationsBlockRubber
						isEditable={
							isEditable &&
							!!cardInfo &&
							(cardInfo.status === TechCardStatuses.progress ||
								cardInfo.status === TechCardStatuses.wasted ||
								userRole === UserRoles.admin ||
								(userRole === UserRoles.storekeeper &&
									cardInfo.status !== TechCardStatuses.accepted_at_sgd &&
									cardInfo.status !== TechCardStatuses.partially_issued))
						}
						focusedBlock={focused}
						setFocusedBlock={handleFocus}
					/>
					<SGDBlockRubber
						isPrinting={isPrinting}
						isEditable={
							isEditable &&
							!!cardInfo &&
							(cardInfo.status === TechCardStatuses.progress ||
								cardInfo.status === TechCardStatuses.wasted ||
								cardInfo.status === TechCardStatuses.accepted_at_sgd ||
								cardInfo.status === TechCardStatuses.partially_issued ||
								userRole === UserRoles.admin ||
								userRole === UserRoles.storekeeper)
						}
						focusedBlock={focused}
						setFocusedBlock={focused.block === 'select_date_conservation' ? handleFocus : undefined}
					/>
				</Flex>
			</div>
		);
	},
);
